import { useEffect } from 'react';
import { useSite } from '../SiteContext';

/**
 * Dynamically updates document <head> favicon when admin changes it in CMS.
 * - Replaces <link rel="icon"> + apple-touch-icon with the CMS-provided URL
 * - Optionally injects a custom HTML snippet from the admin (e.g. multiple icon variants)
 *
 * Renders nothing.
 */
const FaviconLoader = () => {
  const { getSection, loading } = useSite();
  const branding = getSection('branding') || {};
  const customIconUrl = (branding.faviconUrl || '').trim();
  const customHtml = (branding.faviconHtml || '').trim();

  useEffect(() => {
    if (loading) return;

    // 1. Remove any previously-injected <link>/<meta>/<style>/<script> tags from this loader
    document.querySelectorAll('[data-favicon-managed="true"]').forEach((el) => el.remove());

    // 2. Update existing favicon <link> tags if a custom URL is set
    if (customIconUrl) {
      const selectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]',
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((link) => {
          link.setAttribute('href', customIconUrl);
        });
      });
    }

    // 3. Inject any custom HTML the admin pasted (extra <link rel="icon" sizes="...">, etc.)
    if (customHtml) {
      try {
        // Parse the snippet so we can extract individual nodes (link / meta / etc.)
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<head>${customHtml}</head>`, 'text/html');
        Array.from(doc.head.children).forEach((node) => {
          // Skip <script> for safety — admin can re-paste the favicon links if needed
          if (node.tagName === 'SCRIPT') return;
          const clone = node.cloneNode(true);
          clone.setAttribute('data-favicon-managed', 'true');
          document.head.appendChild(clone);
        });
      } catch (err) {
        console.warn('[FaviconLoader] failed to parse custom HTML:', err);
      }
    }
  }, [customIconUrl, customHtml, loading]);

  return null;
};

export default FaviconLoader;
